import { useState } from 'react';
import { Button, Dialog, Typography } from '@equinor/eds-core-react';
import * as StyledDialog from '../../../../styles/addRowDialog/AddRowDialog.styled';
import { ArchelDialogWrapper } from '../ModelArchelMap.styled';
import { ModelArchelMapSelectTable } from './ModelArchelMapSelectTable';
import { CustomPutArchelRequest } from '../types/CustomPutArchelRequest';
import { putApiV1AnalogueModelsByAnalogueModelIdConfigurationByArchelIdArchelMap } from '../../../../api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { analogueModelDefault } from '../../../../constants/analogueModelDefault';
import { useFetchModel } from '../../../../hooks/useFetchModel';
import { useErrorStore } from '../../../../stores/ErrorStore';
import { queryKeys } from '../../../../hooks/queryKeys';
import { useIsOwnerOrAdmin } from '../../../../hooks/useIsOwnerOrAdmin';
/* eslint-disable max-lines-per-function */

export const ModelArchelMapDialog = () => {
  const isOwnerOrAdmin = useIsOwnerOrAdmin();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedArchelMaps, setSelectedArchelMaps] = useState(
    [] as CustomPutArchelRequest[],
  );
  const { data: analogueModel = analogueModelDefault } = useFetchModel();
  const { addError } = useErrorStore();

  const putArchelMap = useMutation({
    mutationFn: async (requestBatch: CustomPutArchelRequest[]) => {
      const result = await Promise.allSettled(
        requestBatch.map(async (req) => {
          try {
            return putApiV1AnalogueModelsByAnalogueModelIdConfigurationByArchelIdArchelMap(
              {
                body: { smdaGeologyStandardId: req.geologyStandardId },
                path: {
                  analogueModelId: analogueModel.analogueModelId,
                  archelId: req.archelId,
                },
              },
            );
          } catch (error) {
            addError(`Failed to add mapping for archel. ${req.archelId}`);
          }
        }),
      );
      return await result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.analogueModel(analogueModel.analogueModelId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.computeSettings(analogueModel.analogueModelId),
      });
      setSelectedArchelMaps([]);
    },
    onError: (error: string) => {
      addError(error);
      setSelectedArchelMaps([]);
    },
  });

  const handleClose = () => {
    setIsOpen(false);
    setSelectedArchelMaps([]);
  };
  const handleSaveClose = () => {
    putArchelMap.mutate(selectedArchelMaps);
    setIsOpen(false);
  };
  const handleOpen = () => {
    setIsOpen(true);
  };
  return (
    <div>
      {isOwnerOrAdmin && (
        <Button
          aria-haspopup="dialog"
          color="primary"
          variant="outlined"
          onClick={handleOpen}
        >
          Map architectural elements
        </Button>
      )}
      <ArchelDialogWrapper
        open={isOpen}
        onClose={handleClose}
        isDismissable={false}
      >
        <Dialog.Header>
          <Dialog.Title>Define architectural elements mapping</Dialog.Title>
        </Dialog.Header>
        <StyledDialog.Content>
          <Typography variant="body_short">
            Select the corresponding element in PEPM for each of the elements
            provided in the model.
          </Typography>

          <ModelArchelMapSelectTable
            selectedArchelMaps={selectedArchelMaps}
            setSelectedArchelMaps={setSelectedArchelMaps}
          />
        </StyledDialog.Content>
        <StyledDialog.Actions>
          <Button onClick={handleSaveClose}>Save and close</Button>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
        </StyledDialog.Actions>
      </ArchelDialogWrapper>
    </div>
  );
};
